import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import moment from 'moment';
import { PollStatus, PollType } from '../types';

const { REACT_APP_GRAPHQL_APIURL } = process.env;

const FIVE_MINUTES_MS = 5 * 60 * 1000;

const pollsListQuery = `query PollsList($abbrv: String!, $limit: Int) {
  pollsList(abbrv: $abbrv, limit: $limit) {
    id
    name
    title
    type
    createdAt
    startTime
    endTime
    status
    voteCount
    mostVotedText
  }
}`;

export interface PollListItem {
  id: string;
  name: string | null;
  title: string | null;
  type: PollType;
  createdAt: string | null;
  startTime: string | null;
  endTime: string | null;
  status: PollStatus;
  voteCount: number;
  mostVotedText: string | null;
}

export interface PollListDataGridFormat {
  id: string;
  name: string;
  title: string;
  type: PollType;
  createdAt: string;
  startTime: string | null;
  endTime: string | null;
  status: PollStatus;
  result: number;
  mostVotedText: string;
}

async function fetchPollsList(abbrv: string, limit?: number): Promise<PollListItem[]> {
  const response = await fetch(REACT_APP_GRAPHQL_APIURL as string, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      query: pollsListQuery,
      variables: { abbrv, limit },
    }),
  });

  if (!response.ok) {
    throw new Error(`Get polls list failed! (${response.status})`);
  }

  const data = await response.json();
  return data?.data?.pollsList ?? [];
}

function convertToDataGridFormat(item: PollListItem): PollListDataGridFormat {
  return {
    id: item.id,
    name: item.name ?? '',
    title: item.title ?? '',
    type: item.type,
    createdAt: item.createdAt ? moment(item.createdAt).format('YYYY-MM-DD HH:mm') : '',
    startTime: item.startTime,
    endTime: item.endTime,
    status: item.status,
    result: item.voteCount,
    mostVotedText: item.mostVotedText ?? '',
  };
}

export function usePollsList(abbrv: string | undefined, limit?: number) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['pollsList', abbrv, limit],
    queryFn: async (): Promise<PollListDataGridFormat[]> => {
      const items = await fetchPollsList(abbrv!, limit);
      return items.map(convertToDataGridFormat);
    },
    enabled: !!abbrv,
    staleTime: FIVE_MINUTES_MS,
    gcTime: FIVE_MINUTES_MS,
  });

  const hardRefresh = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['pollsList', abbrv] });
  }, [queryClient, abbrv]);

  return {
    polls: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    hardRefresh,
  };
}
